import React, { useState, useEffect } from "react";
import { memo } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Header from "./Includes/Header";
import DashboardHeader from "./Includes/DashboardHeader";
import AdminHeader from "./Includes/AdminHeader";
import Footer from "./Includes/Footer";

/* ── Professional inline SVG icons (Heroicons / Lucide style) ── */
const Svg = ({ children, size = 24, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    {children}
  </svg>
);

const IcIdCard    = (p) => <Svg {...p}><rect x="2" y="5" width="20" height="14" rx="2"/><circle cx="8" cy="12" r="2"/><path d="M14 9h4M14 12h4M14 15h2"/></Svg>;
const IcWallet    = (p) => <Svg {...p}><path d="M20 12V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h13a2 2 0 0 0 2-2v-5Z"/><path d="M20 12h-5a2 2 0 0 0 0 4h5"/></Svg>;
const IcBuilding  = (p) => <Svg {...p}><rect x="3" y="3" width="18" height="18" rx="1"/><path d="M9 3v18M15 3v18M3 9h18M3 15h18"/></Svg>;
const IcToken     = (p) => <Svg {...p}><circle cx="12" cy="12" r="9"/><path d="M9 12h6M12 9v6"/></Svg>;
const IcTrendUp   = (p) => <Svg {...p}><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></Svg>;
const IcClock     = (p) => <Svg {...p}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></Svg>;
const IcClipboard = (p) => <Svg {...p}><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/><path d="M9 12h6M9 16h4"/></Svg>;
const IcBook      = (p) => <Svg {...p}><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/><path d="M8 7h8M8 11h8M8 15h5"/></Svg>;
const IcLayers    = (p) => <Svg {...p}><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></Svg>;
const IcSend      = (p) => <Svg {...p}><path d="M22 2 11 13"/><path d="M22 2 15 22 11 13 2 9l20-7z"/></Svg>;
const IcCheck     = (p) => <Svg {...p}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></Svg>;
const IcShield    = (p) => <Svg {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><rect x="9" y="11" width="6" height="5" rx="1"/><path d="M12 11V9a2 2 0 1 1 4 0v2"/></Svg>;
const IcLink      = (p) => <Svg {...p}><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></Svg>;
const IcScale     = (p) => <Svg {...p}><line x1="12" y1="3" x2="12" y2="21"/><path d="M3 9l9-6 9 6"/><path d="M3 9l4 8H3l4-8zM17 9l4 8h-8l4-8z"/></Svg>;
const IcEye       = (p) => <Svg {...p}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></Svg>;
const IcUsers     = (p) => <Svg {...p}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></Svg>;
const IcHome      = (p) => <Svg {...p}><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></Svg>;

const HowItWork = () => {
  const { t } = useTranslation();
  const [header, setHeader] = useState("guest");
  const { auth } = useSelector((state) => state);

  useEffect(() => {
    if (auth.data) {
      const response = auth.data;
      if (response.status === 1 && response.user) {
        setHeader(response.user.role !== 1 ? "dashboard" : "admin");
      } else {
        setHeader("guest");
      }
    }
  }, [auth]);

  const sectionLabel = {
    color: "#c9a84c", fontWeight: 600, letterSpacing: "2px", fontSize: "12px",
    textTransform: "uppercase", marginBottom: "12px", display: "block"
  };
  const sectionH2 = {
    fontFamily: "'DM Serif Display', serif", fontSize: "clamp(28px, 3vw, 38px)",
    color: "#0e3725", margin: "0 0 16px", fontWeight: 400
  };

  const investorSteps = [
    { num: "01", Ic: IcIdCard,   title: t("howItWorks.inv1Title"), desc: t("howItWorks.inv1Desc") },
    { num: "02", Ic: IcWallet,   title: t("howItWorks.inv2Title"), desc: t("howItWorks.inv2Desc") },
    { num: "03", Ic: IcBuilding, title: t("howItWorks.inv3Title"), desc: t("howItWorks.inv3Desc") },
    { num: "04", Ic: IcToken,    title: t("howItWorks.inv4Title"), desc: t("howItWorks.inv4Desc") },
    { num: "05", Ic: IcTrendUp,  title: t("howItWorks.inv5Title"), desc: t("howItWorks.inv5Desc") },
    { num: "06", Ic: IcClock,    title: t("howItWorks.inv6Title"), desc: t("howItWorks.inv6Desc") },
  ];

  const developerSteps = [
    { num: "01", Ic: IcClipboard, title: t("howItWorks.dev1Title"), desc: t("howItWorks.dev1Desc") },
    { num: "02", Ic: IcBook,      title: t("howItWorks.dev2Title"), desc: t("howItWorks.dev2Desc") },
    { num: "03", Ic: IcLayers,    title: t("howItWorks.dev3Title"), desc: t("howItWorks.dev3Desc") },
    { num: "04", Ic: IcSend,      title: t("howItWorks.dev4Title"), desc: t("howItWorks.dev4Desc") },
    { num: "05", Ic: IcCheck,     title: t("howItWorks.dev5Title"), desc: t("howItWorks.dev5Desc") },
  ];

  const securityPoints = [
    { Ic: IcShield, title: t("howItWorks.sec1Title"), desc: t("howItWorks.sec1Desc") },
    { Ic: IcLink,   title: t("howItWorks.sec2Title"), desc: t("howItWorks.sec2Desc") },
    { Ic: IcScale,  title: t("howItWorks.sec3Title"), desc: t("howItWorks.sec3Desc") },
    { Ic: IcEye,    title: t("howItWorks.sec4Title"), desc: t("howItWorks.sec4Desc") },
  ];

  return (
    <div style={{ fontFamily: "'Inter', sans-serif", background: "#f4f4f2" }}>
      {header === "guest" ? <Header /> : header === "dashboard" ? <DashboardHeader /> : <AdminHeader />}
      <div className="main_content">

        {/* Hero */}
        <section className="hero-section" style={{ background: "linear-gradient(135deg, #0e3725 0%, #1a5c3a 60%, #0e3725 100%)", padding: "80px 0 60px", textAlign: "center", color: "#fff" }}>
          <div style={{ maxWidth: "800px", margin: "0 auto", padding: "0 24px" }}>
            <span style={sectionLabel}>{t("howItWorks.processLabel")}</span>
            <h1 className="hero-title" style={{ fontSize: "clamp(36px, 5vw, 56px)", fontWeight: 600, margin: "0 0 20px", lineHeight: 1.15 }}>
              {t("howItWorks.heroTitle")}
            </h1>
            <p style={{ fontSize: "18px", color: "rgba(255,255,255,0.82)", lineHeight: 1.7, maxWidth: "640px", margin: "0 auto" }}>
              {t("howItWorks.heroSubtitle")}
            </p>
          </div>
        </section>

        {/* Platform Overview */}
        <section style={{ background: "#fff", padding: "64px 0" }}>
          <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "0 24px", textAlign: "center" }}>
            <span style={sectionLabel}>{t("howItWorks.overviewLabel")}</span>
            <h2 style={{ ...sectionH2, marginBottom: "48px" }}>{t("howItWorks.overviewTitle")}</h2>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "32px" }}>
              {[
                { title: t("howItWorks.forInvestors"), Ic: IcUsers, color: "#0e3725",
                  desc: t("howItWorks.forInvestorsDesc") },
                { title: t("howItWorks.forDevelopers"), Ic: IcHome, color: "#c9a84c",
                  desc: t("howItWorks.forDevelopersDesc") },
              ].map((card, i) => (
                <div key={i} style={{ background: "#f4f4f2", borderRadius: "16px", padding: "40px 32px", borderTop: `4px solid ${card.color}`, textAlign: "left" }}>
                  <div style={{ marginBottom: "16px" }}>
                    <card.Ic size={40} color={card.color} />
                  </div>
                  <h3 style={{ fontFamily: "'DM Serif Display', serif", fontSize: "24px", color: "#0e3725", margin: "0 0 12px", fontWeight: 400 }}>{card.title}</h3>
                  <p style={{ color: "#666", lineHeight: 1.8, fontSize: "15px", margin: 0 }}>{card.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Investor Journey */}
        <section style={{ background: "#f4f4f2", padding: "72px 0" }}>
          <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "0 24px" }}>
            <div style={{ textAlign: "center", marginBottom: "56px" }}>
              <span style={sectionLabel}>{t("howItWorks.investorJourneyLabel")}</span>
              <h2 style={sectionH2}>{t("howItWorks.investorJourneyTitle")}</h2>
              <p style={{ color: "#666", fontSize: "16px", maxWidth: "500px", margin: "0 auto" }}>{t("howItWorks.investorJourneySubtitle")}</p>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "24px" }}>
              {investorSteps.map((step, i) => (
                <div key={i} style={{ background: "#fff", borderRadius: "16px", padding: "32px 28px", border: "1px solid rgba(14,55,37,0.08)", boxShadow: "0 2px 12px rgba(14,55,37,0.04)", display: "flex", gap: "20px", alignItems: "flex-start" }}>
                  <div style={{ flexShrink: 0, width: "52px", height: "52px", borderRadius: "14px", background: "#0e3725", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <step.Ic size={24} color="#c9a84c" />
                  </div>
                  <div>
                    <span style={{ color: "#c9a84c", fontSize: "11px", fontWeight: 700, letterSpacing: "2px" }}>STEP {step.num}</span>
                    <h4 style={{ color: "#0e3725", fontSize: "16px", fontWeight: 700, margin: "4px 0 8px" }}>{step.title}</h4>
                    <p style={{ color: "#666", fontSize: "14px", lineHeight: 1.7, margin: 0 }}>{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Developer Journey */}
        <section style={{ background: "#0e3725", padding: "72px 0" }}>
          <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "0 24px" }}>
            <div style={{ textAlign: "center", marginBottom: "56px" }}>
              <span style={{ ...sectionLabel, color: "#c9a84c" }}>{t("howItWorks.developerJourneyLabel")}</span>
              <h2 style={{ fontFamily: "inherit", fontSize: "clamp(28px, 3vw, 38px)", color: "#fff", margin: "0 0 16px", fontWeight: 700 }}>{t("howItWorks.developerJourneyTitle")}</h2>
              <p style={{ color: "rgba(255,255,255,0.7)", fontSize: "16px", maxWidth: "500px", margin: "0 auto" }}>{t("howItWorks.developerJourneySubtitle")}</p>
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              {developerSteps.map((step, i) => (
                <div key={i} style={{ display: "flex", gap: "24px", alignItems: "flex-start", paddingBottom: i < developerSteps.length - 1 ? "32px" : "0", borderLeft: i < developerSteps.length - 1 ? "2px solid rgba(201,168,76,0.3)" : "2px solid transparent", marginLeft: "25px", paddingLeft: "32px", position: "relative" }}>
                  <div style={{ position: "absolute", left: "-26px", top: "0", width: "50px", height: "50px", borderRadius: "50%", background: "rgba(201,168,76,0.12)", border: "2px solid #c9a84c", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <step.Ic size={22} color="#c9a84c" />
                  </div>
                  <div style={{ paddingLeft: "8px" }}>
                    <span style={{ color: "rgba(201,168,76,0.8)", fontSize: "11px", fontWeight: 700, letterSpacing: "2px" }}>STEP {step.num}</span>
                    <h4 style={{ color: "#fff", fontSize: "17px", fontWeight: 700, margin: "4px 0 8px" }}>{step.title}</h4>
                    <p style={{ color: "rgba(255,255,255,0.65)", fontSize: "14px", lineHeight: 1.7, margin: 0 }}>{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Security */}
        <section style={{ background: "#fff", padding: "72px 0" }}>
          <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "0 24px" }}>
            <div style={{ textAlign: "center", marginBottom: "48px" }}>
              <span style={sectionLabel}>{t("howItWorks.securityLabel")}</span>
              <h2 style={sectionH2}>{t("howItWorks.securityTitle")}</h2>
              <p style={{ color: "#666", fontSize: "16px", maxWidth: "500px", margin: "0 auto" }}>{t("howItWorks.securitySubtitle")}</p>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "24px" }}>
              {securityPoints.map((point, i) => (
                <div key={i} style={{ background: "#f4f4f2", borderRadius: "16px", padding: "32px 24px", borderBottom: "3px solid #0e3725" }}>
                  <div style={{ marginBottom: "16px" }}>
                    <point.Ic size={36} color="#0e3725" />
                  </div>
                  <h4 style={{ color: "#0e3725", fontSize: "16px", fontWeight: 700, margin: "0 0 10px" }}>{point.title}</h4>
                  <p style={{ color: "#666", fontSize: "14px", lineHeight: 1.7, margin: 0 }}>{point.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section style={{ background: "linear-gradient(135deg, #c9a84c 0%, #a8873a 100%)", padding: "72px 0", textAlign: "center" }}>
          <div style={{ maxWidth: "600px", margin: "0 auto", padding: "0 24px" }}>
            <h2 style={{ fontFamily: "inherit", fontSize: "clamp(28px, 4vw, 42px)", color: "#fff", margin: "0 0 16px", fontWeight: 700 }}>
              {t("howItWorks.ctaTitle")}
            </h2>
            <p style={{ color: "rgba(255,255,255,0.9)", fontSize: "17px", lineHeight: 1.7, marginBottom: "36px" }}>
              {t("howItWorks.ctaSubtitle")}
            </p>
            <div style={{ display: "flex", gap: "16px", justifyContent: "center", flexWrap: "wrap" }}>
              <Link to="/register" style={{ padding: "16px 36px", background: "#0e3725", color: "#fff", borderRadius: "8px", fontWeight: 700, fontSize: "16px", textDecoration: "none", letterSpacing: "0.5px" }}>
                {t("howItWorks.ctaButton")}
              </Link>
              <Link to="/about" style={{ padding: "16px 36px", background: "transparent", color: "#fff", border: "2px solid rgba(255,255,255,0.7)", borderRadius: "8px", fontWeight: 700, fontSize: "16px", textDecoration: "none" }}>
                {t("common.learnMore")}
              </Link>
            </div>
          </div>
        </section>

      </div>
      <Footer />
    </div>
  );
};

export default memo(HowItWork);
